/**
 * Shared PII / secret redaction for telemetry.
 *
 * Two layers, used across the telemetry stack:
 *   1. KEY deny-list — property keys whose NAME signals sensitive material
 *      (auth / identity / secrets). Their value is dropped entirely regardless
 *      of content (`sanitizeTelemetryProperties` in telemetryPreferences.ts).
 *   2. CONTENT redaction — pattern-based scrubbing of free-text values, log
 *      messages, error messages/stacks, and URLs before they leave the browser
 *      for a third-party sink (Datadog RUM/Logs `beforeSend` in datadog.ts).
 *
 * The content patterns are deliberately conservative (low false-positive) and
 * cover the highest-risk leaks: email addresses, JWTs, and Bearer tokens.
 */

// Case-insensitive substrings that mark a property KEY as sensitive. Matches
// common auth/identity/secret field names (and their snake/kebab variants).
const SENSITIVE_KEY_RE =
  /(e[-_]?mail|password|passwd|secret|credential|authorization|login[-_]?hint|api[-_]?key|access[-_]?token|refresh[-_]?token|id[-_]?token|\btoken\b|\bjwt\b|\bbearer\b|session[-_]?id|\bcookie\b|\bssn\b)/i;

export const REDACTED_SENSITIVE_KEY = '[redacted:sensitive-key]';

export const isSensitiveTelemetryKey = (key: string): boolean => SENSITIVE_KEY_RE.test(key);

// Content patterns redacted from any free-text string before it is forwarded.
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Compact JWT: three base64url segments separated by dots, starting with the
// canonical `eyJ` header prefix (keeps false positives near zero).
const JWT_RE = /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g;
// `Bearer <token>` (Authorization-header style).
const BEARER_RE = /\b[Bb]earer\s+[A-Za-z0-9._~+/-]+=*/g;

/**
 * Redacts email addresses, JWTs, and Bearer tokens from a string. Returns the
 * input unchanged when there is nothing to redact (and passes through non-string
 * / empty values untouched so callers can apply it defensively).
 */
export const redactSensitiveText = (text: string): string => {
  if (!text) return text;
  return text
    .replace(JWT_RE, '[redacted-jwt]')
    .replace(BEARER_RE, 'Bearer [redacted]')
    .replace(EMAIL_RE, '[redacted-email]');
};
